// vg-binding: P7.D-46
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/features/payouts/BankAccountsManager.tsx
//
// Inline CRUD for vendor bank accounts (Wave 11 add-on — surface for
// the Wave 10 BE CRUD endpoints).

import { useState } from 'react';

import { cn } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import {
  useBankAccounts,
  useCreateBank,
  useDeleteBank,
  useSetDefaultBank,
  type CreateBankInput,
  type WalletApiError,
} from '../wallet/api.js';

const FIELD_INPUT_CLASS =
  'h-9 w-full rounded border border-pw-border bg-pw-bg px-2 text-xs focus:border-pw-primary focus:outline-none';

export function BankAccountsManager(): JSX.Element {
  const { t } = useTranslation('wallet');
  const banksQ = useBankAccounts();
  const createM = useCreateBank();
  const deleteM = useDeleteBank();
  const setDefaultM = useSetDefaultBank();

  const [draft, setDraft] = useState<CreateBankInput>({
    method: 'bank',
    account_holder: '',
    account_number: '',
    bank_name: '',
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Payoneer / Wise identify the payee by EMAIL — the account_number field
  // holds that email. Validate format so a malformed payout target is
  // caught before it reaches the BE (which would otherwise accept any
  // string and fail silently at payout time).
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const methodNeedsEmail = draft.method === 'payoneer' || draft.method === 'wise';
  const accountNumberTrimmed = draft.account_number.trim();
  const emailInvalid =
    methodNeedsEmail &&
    accountNumberTrimmed.length > 0 &&
    !EMAIL_RE.test(accountNumberTrimmed);

  const canSubmit =
    draft.account_holder.trim().length > 0 &&
    accountNumberTrimmed.length > 0 &&
    (draft.method !== 'bank' || (draft.bank_name ?? '').trim().length > 0) &&
    (!methodNeedsEmail || EMAIL_RE.test(accountNumberTrimmed));

  async function handleAdd(): Promise<void> {
    setErrorMsg(null);
    try {
      const payload: CreateBankInput = {
        method: draft.method,
        account_holder: draft.account_holder.trim(),
        account_number: draft.account_number.trim(),
      };
      if (draft.method === 'bank' && draft.bank_name) {
        payload.bank_name = draft.bank_name.trim();
      }
      await createM.mutateAsync(payload);
      setDraft({ method: 'bank', account_holder: '', account_number: '', bank_name: '' });
    } catch (err) {
      const e = err as WalletApiError;
      setErrorMsg(e.userMessage ?? e.message);
    }
  }

  return (
    <section
      data-testid="bank-accounts-manager"
      className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
    >
      <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('banks.sectionTitle')}</h3>

      {banksQ.isLoading ? (
        <div className="h-12 animate-pulse rounded-md border border-pw-border bg-pw-surface" />
      ) : (banksQ.data?.rows ?? []).length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-4 text-center text-sm text-pw-text-muted">
          {t('banks.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {(banksQ.data?.rows ?? []).map((b) => (
            <li
              key={b.bank_id}
              data-testid={`bank-row-${b.bank_id}`}
              className={cn(
                'flex items-center justify-between gap-3 rounded-md border border-pw-border bg-pw-surface px-3 py-2',
                b.is_default && 'ring-1 ring-pw-primary',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-pw-text">
                  {b.method.toUpperCase()} · {b.account_holder}
                  {b.is_default ? <span className="ml-2 text-xs text-pw-primary">{t('banks.defaultBadge')}</span> : null}
                </p>
                <p className="truncate text-xs text-pw-text-muted">
                  {b.account_number} · {b.bank_name ?? '—'}{' '}
                  {b.is_verified ? (
                    <span className="text-green-700">{t('banks.verified')}</span>
                  ) : (
                    <span className="text-yellow-700">{t('banks.pendingVerify')}</span>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {!b.is_default ? (
                  <button
                    type="button"
                    data-testid={`bank-set-default-${b.bank_id}`}
                    onClick={() => void setDefaultM.mutateAsync(b.bank_id)}
                    disabled={setDefaultM.isPending}
                    className="rounded border border-pw-border bg-pw-bg px-2 py-1 text-xs text-pw-text-muted hover:bg-pw-surface"
                  >
                    {t('banks.setDefault')}
                  </button>
                ) : null}
                <button
                  type="button"
                  data-testid={`bank-delete-${b.bank_id}`}
                  onClick={() => {
                    if (confirm(t('banks.deleteConfirm'))) {
                      void deleteM.mutateAsync(b.bank_id);
                    }
                  }}
                  disabled={deleteM.isPending}
                  className="rounded border border-pw-border bg-pw-bg px-2 py-1 text-xs text-pw-text-muted hover:bg-red-50 hover:text-red-700"
                >
                  {t('banks.deleteButton')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 rounded-md border border-pw-border bg-pw-surface p-3">
        <p className="mb-2 text-sm font-medium text-pw-text">{t('banks.addSectionTitle')}</p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <select
            data-testid="bank-add-method"
            className={FIELD_INPUT_CLASS}
            value={draft.method}
            onChange={(e) => setDraft({ ...draft, method: e.target.value as CreateBankInput['method'] })}
          >
            <option value="bank">{t('banks.methodBank')}</option>
            <option value="payoneer">{t('banks.methodPayoneer')}</option>
            <option value="wise">{t('banks.methodWise')}</option>
          </select>
          <input
            type="text"
            data-testid="bank-add-holder"
            placeholder={t('banks.holderPlaceholder')}
            className={FIELD_INPUT_CLASS}
            value={draft.account_holder}
            onChange={(e) => setDraft({ ...draft, account_holder: e.target.value })}
          />
          <input
            type={methodNeedsEmail ? 'email' : 'text'}
            data-testid="bank-add-number"
            placeholder={draft.method === 'bank' ? t('banks.numberPlaceholderBank') : t('banks.numberPlaceholderOther')}
            className={cn(
              FIELD_INPUT_CLASS,
              emailInvalid && 'border-red-400 focus:border-red-500',
            )}
            value={draft.account_number}
            onChange={(e) => setDraft({ ...draft, account_number: e.target.value })}
            aria-invalid={emailInvalid || undefined}
          />
          {draft.method === 'bank' ? (
            <input
              type="text"
              data-testid="bank-add-bankname"
              placeholder={t('banks.bankNamePlaceholder')}
              className={FIELD_INPUT_CLASS}
              value={draft.bank_name ?? ''}
              onChange={(e) => setDraft({ ...draft, bank_name: e.target.value })}
            />
          ) : null}
        </div>
        {emailInvalid ? (
          <p
            data-testid="bank-add-email-error"
            className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700"
          >
            {t('banks.emailInvalid')}
          </p>
        ) : null}
        {errorMsg ? (
          <p className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
            {errorMsg}
          </p>
        ) : null}
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            data-testid="bank-add-submit"
            disabled={!canSubmit || createM.isPending}
            onClick={() => void handleAdd()}
            className="rounded-md bg-pw-primary px-4 py-2 text-xs font-semibold text-white hover:bg-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createM.isPending ? t('banks.addSubmitPending') : t('banks.addSubmitIdle')}
          </button>
        </div>
      </div>
    </section>
  );
}

export default BankAccountsManager;
